// src/pages/PublicStudio.js
import { useEffect, useState, useMemo } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { publicAPI, publicStudioAPI, stripeAPI } from "../lib/api";
import { toast } from "sonner";
import BookingModal from "../components/BookingModal";
import PracticeTracking from "../components/tracking/PracticeTracking";
import { emitTrackingEvent } from "../lib/tracking/runtime";
import { buildBookingSuccessRedirectUrl } from "../lib/tracking/redirect";
import { erfasseHerkunft } from "../lib/acquisitionTracking";
import { Clock, MapPin, Mail, Phone, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Check, XCircle } from "lucide-react";
import GoogleReviewsCarousel from "../components/GoogleReviewsCarousel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { formatCurrency } from "../lib/utils";
import { resolveDesignTheme } from "../lib/designThemes";
import LinkifiedText from "../components/LinkifiedText";

// Zeitformat aus DB (HH:MM:SS) in "HH:MM Uhr"
const formatDbTime = (t) => (t ? `${t.slice(0, 5)} Uhr` : "");

// Wochentage (kurz) passend zu weekday 0=Mo
const WEEKDAYS_SHORT = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const WEEKDAYS = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];

function groupAvailability(availability) {
  if (!availability.length) return [];

  const a = [...availability].sort((x, y) => x.weekday - y.weekday);
  const groups = [];
  let i = 0;

  while (i < a.length) {
    const start = a[i];
    let j = i;

    while (
      j + 1 < a.length &&
      a[j + 1].weekday === a[j].weekday + 1 &&
      a[j + 1].start_time === start.start_time &&
      a[j + 1].end_time === start.end_time
    ) {
      j++;
    }

    groups.push({
      startDay: a[i].weekday,
      endDay: a[j].weekday,
      start_time: start.start_time,
      end_time: start.end_time,
    });

    i = j + 1;
  }

  return groups;
}

function formatGroupLabel(g) {
  if (g.startDay === g.endDay) return WEEKDAYS_SHORT[g.startDay];
  return `${WEEKDAYS_SHORT[g.startDay]}-${WEEKDAYS_SHORT[g.endDay]}`;
}

function formatStudioMoney(price, currency) {
  const n = Number(price || 0);
  if (!Number.isFinite(n) || n <= 0) return "Kostenlos";
  return formatCurrency(n, currency || "EUR");
}

function servicePriceStartsAt(service) {
  return Number(service?.price || 0) > 0 && service?.price_starts_at === true;
}

function formatStudioServicePrice(service, currency) {
  const label = formatStudioMoney(service?.price ?? 0, currency);
  return servicePriceStartsAt(service) ? `ab ${label}` : label;
}

function shouldShowServicePrice(service) {
  return service?.show_price !== false;
}

function hasComparePrice(service) {
  const price = Number(service?.price || 0);
  const comparePrice = Number(service?.compare_price || 0);
  return shouldShowServicePrice(service) && Number.isFinite(comparePrice) && comparePrice > price && comparePrice > 0;
}

function formatPriceEUR(price) {
  const n = Number(price || 0);
  if (!Number.isFinite(n) || n <= 0) return "Kostenlos";
  return formatCurrency(n, "EUR");
}

function getPrimaryCategoryMeta(service) {
  if (!service) return { id: "__uncategorized__", name: "Weitere Leistungen", sort: 9999, banner_image_url: null };
  if (service.category_primary_id && service.category_primary) {
    return {
      id: String(service.category_primary_id),
      name: service.category_primary,
      sort: service.category_primary_sort ?? service.category_sort_order ?? 9999,
      banner_image_url: service.category_primary_banner_image_url || null,
    };
  }
  if (Array.isArray(service?.service_categories) && service.service_categories.length > 0) {
    const first = service.service_categories[0];
    return {
      id: String(first.id || first.name || "__uncategorized__"),
      name: first.name || "Weitere Leistungen",
      sort: first.sort_order ?? 9999,
      banner_image_url: first.banner_image_url || null,
    };
  }
  const name =
    service.category_primary ||
    (Array.isArray(service?.category_names) ? service.category_names[0] : null) ||
    "Weitere Leistungen";
  const sort = service.category_primary_sort ?? service.category_sort_order ?? 9999;
  const banner =
    service.category_primary_banner_image_url ||
    (Array.isArray(service?.service_categories) && service.service_categories[0]?.banner_image_url) ||
    null;
  return { id: name, name, sort, banner_image_url: banner };
}

function getServiceCategoryMetaList(service) {
  if (Array.isArray(service?.service_categories) && service.service_categories.length > 0) {
    return service.service_categories
      .map((category) => ({
        id: String(category.id || category.name || "__uncategorized__"),
        name: category.name || "Weitere Leistungen",
        sort: category.sort_order ?? 9999,
        banner_image_url: category.banner_image_url || null,
      }))
      .sort((a, b) => {
        const sortDiff = Number(a.sort ?? 999999) - Number(b.sort ?? 999999);
        if (sortDiff !== 0) return sortDiff;
        return String(a.name || "").localeCompare(String(b.name || ""), "de");
      });
  }
  return [getPrimaryCategoryMeta(service)];
}

function compareServicesBySortOrder(a, b) {
  const sortDiff = Number(a.sort_order ?? 999999) - Number(b.sort_order ?? 999999);
  if (sortDiff !== 0) return sortDiff;
  return String(a.name || "").localeCompare(String(b.name || ""), "de");
}

export default function PublicStudio() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();


  // Praxen mit eigener Domain landen hier statt auf /book. Ohne diese
  // Zeile ginge die Herkunft genau bei ihnen verloren.
  useEffect(() => {
    erfasseHerkunft(window.location.search, document.referrer);
  }, []);
  const [loading, setLoading] = useState(true);
  const [studio, setStudio] = useState(null);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [googleReviews, setGoogleReviews] = useState(null);
  const [bookingModal, setBookingModal] = useState({ open: false, service: null });
  const [productOrderDialogOpen, setProductOrderDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOrderLoading, setProductOrderLoading] = useState(false);
  const [productOrderForm, setProductOrderForm] = useState({
    quantity: 1,
    client_name: "",
    client_email: "",
    client_phone: "",
    client_street: "",
    client_postal_code: "",
    client_city: "",
    notes: "",
  });
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showAllHours, setShowAllHours] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("Alle");
  const [paymentModal, setPaymentModal] = useState({ open: false, type: null, message: "" });
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [openCategoryNames, setOpenCategoryNames] = useState([]);

  // Theme basierend auf Studio-Einstellung
  const theme = useMemo(() => {
    return resolveDesignTheme(studio || {});
  }, [studio]);

  const categories = useMemo(() => {
    const map = new Map();
    services.forEach((svc) => {
      getServiceCategoryMetaList(svc).forEach((cat) => {
        if (!cat?.name) return;
        if (!map.has(cat.name)) {
          map.set(cat.name, { name: cat.name, sort: cat.sort, banner_image_url: cat.banner_image_url });
        }
      });
    });
    return Array.from(map.values()).sort((a, b) => (a.sort ?? 9999) - (b.sort ?? 9999));
  }, [services]);

  const categoryOptions = useMemo(() => {
    const names = categories.map((g) => g.name).filter(Boolean);
    const unique = names.filter((v, i, a) => a.indexOf(v) === i);
    return ["Alle", ...unique];
  }, [categories]);

  const serviceGroups = useMemo(() => {
    const map = new Map();
    services.filter(Boolean).forEach((service) => {
      getServiceCategoryMetaList(service).forEach((category) => {
        if (selectedCategory && selectedCategory !== "Alle" && category.name !== selectedCategory) return;
        if (!map.has(category.name)) {
          map.set(category.name, { ...category, services: [] });
        }
        const group = map.get(category.name);
        if (!group.services.some((entry) => String(entry.id) === String(service.id))) {
          group.services.push(service);
        }
      });
    });
    return Array.from(map.values())
      .map((group) => ({ ...group, services: [...group.services].sort(compareServicesBySortOrder) }))
      .sort((a, b) => {
        const sortDiff = Number(a.sort ?? 999999) - Number(b.sort ?? 999999);
        if (sortDiff !== 0) return sortDiff;
        return String(a.name || "").localeCompare(String(b.name || ""), "de");
      });
  }, [services, selectedCategory]);
  const categoryDropdownMode = studio?.service_category_dropdown_mode || "none";
  const shouldUseCategoryDropdowns = categoryDropdownMode !== "none" && selectedCategory === "Alle";

  useEffect(() => {
    if (!shouldUseCategoryDropdowns || serviceGroups.length === 0) {
      setOpenCategoryNames([]);
      return;
    }
    if (categoryDropdownMode === "all_open") {
      setOpenCategoryNames(serviceGroups.map((group) => String(group.name)));
      return;
    }
    setOpenCategoryNames([String(serviceGroups[0].name)]);
  }, [categoryDropdownMode, serviceGroups, shouldUseCategoryDropdowns]);

  // Ein Link kann direkt eine Leistung oeffnen: ?service=<id>. Gebraucht fuer
  // Links, die die Praxis selbst verschickt - aus dem Messenger, per WhatsApp
  // oder aus der Instagram-Bio. Ohne das muesste die Patientin die Leistung in
  // einer langen Liste selbst suchen, und der Termin waere haeufiger der
  // falschen Leistung zugeordnet.
  useEffect(() => {
    if (!services.length) return;
    const gewuenscht = new URLSearchParams(location.search).get("service");
    if (!gewuenscht) return;
    const treffer = services.find((svc) => String(svc.id) === String(gewuenscht));
    if (treffer) setBookingModal({ open: true, service: treffer });
  }, [services, location.search]);

  // Payment return handling
  useEffect(() => {
    let active = true;
    const sp = new URLSearchParams(location.search);
    const payment = sp.get("payment");
    const checkoutId = sp.get("checkout_id");
    const appointmentId = sp.get("appointment_id");
    const sessionId = sp.get("session_id");
    const productPayment = sp.get("product_payment");

    if (!payment && !productPayment) {
      return undefined;
    }

    if (productPayment === "success") {
      setPaymentModal({ open: true, type: "success", message: "Vielen Dank! Deine Produktbestellung wurde bezahlt." });
      return undefined;
    }
    if (productPayment === "cancelled") {
      setPaymentModal({ open: true, type: "cancelled", message: "Zahlung abgebrochen." });
      return undefined;
    }

    if (!checkoutId && !appointmentId) {
      if (payment === "cancelled") {
        setPaymentModal({ open: true, type: "cancelled", message: "Die Zahlung wurde nicht abgeschlossen. Es wurde kein Termin gebucht." });
      }
      return undefined;
    }

    (async () => {
      try {
        if (payment === "success") {
          const res = checkoutId
            ? await publicAPI.finalizeCheckoutPayment(checkoutId, sessionId)
            : await publicAPI.finalizeAppointmentPayment(appointmentId, sessionId);
          const data = res?.data || {};
          if (!active) return;
          if (data.ok) {
            await emitTrackingEvent(data.tracking_event);
            if (!active) return;
            const redirectUrl = buildBookingSuccessRedirectUrl(studio?.booking_success_redirect_url);
            if (redirectUrl) {
              window.location.href = redirectUrl;
              return;
            }
            setPaymentModal({
              open: true,
              type: "success",
              message: data.requires_confirmation
                ? "Zahlung erfolgreich. Deine Anfrage ist eingegangen und wird nun bestätigt."
                : "Zahlung erfolgreich. Dein Termin ist bestätigt.",
            });
          } else {
            setPaymentModal({
              open: true,
              type: "cancelled",
              message: data.message || "Der gewählte Termin war leider nicht mehr verfügbar. Die Zahlung wurde automatisch rückerstattet.",
            });
          }
          return;
        }

        let appointmentKept = false;
        if (checkoutId) {
          await publicAPI.cancelCheckoutPayment(checkoutId).catch(() => {});
        } else if (appointmentId) {
          const cancelResponse = await publicAPI.cancelAppointmentPayment(appointmentId).catch(() => null);
          appointmentKept = Boolean(cancelResponse?.data?.appointment_kept);
        }
        if (!active) return;
        setPaymentModal({
          open: true,
          type: "cancelled",
          message: appointmentKept
            ? "Die Zahlung wurde nicht abgeschlossen. Dein Termin bleibt reserviert und du kannst denselben Zahlungslink später erneut öffnen."
            : "Die Zahlung wurde nicht abgeschlossen. Es wurde kein Termin gebucht.",
        });
      } catch (error) {
        if (!active) return;
        setPaymentModal({
          open: true,
          type: "cancelled",
          message: error?.response?.data?.detail || "Der Zahlungsstatus konnte nicht verarbeitet werden.",
        });
      } finally {
      }
    })();

    return () => {
      active = false;
    };
  }, [location.search, studio?.booking_success_redirect_url]);

  useEffect(() => {
    loadStudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadStudio = async () => {
    setLoading(true);
    try {
      const [studioRes, servicesRes, productsRes, galleryRes, availRes] = await Promise.all([
        publicStudioAPI.getStudio(slug),
        publicStudioAPI.getServices(slug),
        publicStudioAPI.getProducts(slug),
        publicStudioAPI.getGallery(slug),
        publicStudioAPI.getOpeningHours(slug),
      ]);

      setStudio(studioRes.data);
      if (studioRes.data?.google_place_id) {
        publicStudioAPI.getGoogleReviews(slug).then((res) => {
          if (res.data && res.data.reviews?.length) setGoogleReviews(res.data);
        }).catch(() => {});
      }
      const nextServices = Array.isArray(servicesRes.data) ? servicesRes.data : [];
      setServices(nextServices);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      setGalleryImages(Array.isArray(galleryRes.data) ? galleryRes.data : []);
      setAvailability(Array.isArray(availRes.data) ? availRes.data : []);
    } catch (error) {
      console.error("PublicStudio load failed:", error);
      toast.error(error.message || "Fehler beim Laden der öffentlichen Seite");
      setStudio(null);
    } finally {
      setLoading(false);
    }
  };

  // Prüfe ob gerade geöffnet
  const isCurrentlyOpen = () => {
    const now = new Date();
    const dayOfWeek = now.getDay() === 0 ? 6 : now.getDay() - 1; // Konvertiere zu Mo=0
    const currentTime = now.toTimeString().slice(0, 5);

    const todayAvail = availability.find((a) => a.weekday === dayOfWeek);
    if (!todayAvail) return false;

    return currentTime >= todayAvail.start_time && currentTime <= todayAvail.end_time;
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const openProductOrder = (product) => {
    setSelectedProduct(product);
    setProductOrderForm({
      quantity: 1,
      client_name: "",
      client_email: "",
      client_phone: "",
      client_street: "",
      client_postal_code: "",
      client_city: "",
      notes: "",
    });
    setProductOrderDialogOpen(true);
  };

  const submitProductOrder = async () => {
    if (!selectedProduct?.id || !studio?.org_slug) return;
    if (!productOrderForm.client_name || !productOrderForm.client_email) {
      toast.error("Bitte Name und E-Mail eingeben.");
      return;
    }
    if (!productOrderForm.client_street || !productOrderForm.client_postal_code || !productOrderForm.client_city) {
      toast.error("Bitte Straße, PLZ und Stadt eingeben.");
      return;
    }
    const quantity = Number(productOrderForm.quantity || 1);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      toast.error("Bitte eine gültige Menge eingeben.");
      return;
    }
    setProductOrderLoading(true);
    try {
      const currentUrl = window.location.href.split("?")[0];
      const res = await stripeAPI.createProductCheckoutSession({
        org_slug: studio.org_slug,
        product_id: selectedProduct.id,
        quantity: Math.floor(quantity),
        client_name: productOrderForm.client_name,
        client_email: productOrderForm.client_email,
        client_phone: productOrderForm.client_phone || null,
        client_street: productOrderForm.client_street || null,
        client_postal_code: productOrderForm.client_postal_code || null,
        client_city: productOrderForm.client_city || null,
        notes: productOrderForm.notes || null,
        location_id: studio.id || null,
        success_url: `${currentUrl}?product_payment=success`,
        cancel_url: `${currentUrl}?product_payment=cancelled`,
      });
      const checkoutUrl = res.data.checkout_url;
      if (!checkoutUrl) throw new Error("Checkout-URL fehlt");
      window.location.assign(checkoutUrl);
    } catch (error) {
      toast.error(error.response.data.detail || "Checkout konnte nicht gestartet werden.");
    } finally {
      setProductOrderLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!studio) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-subtle">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Standort nicht gefunden</h2>
            <p className="text-gray-600 mb-6">Der gesuchte Standort existiert nicht oder ist nicht aktiv.</p>
            <Button asChild>
              <Link to="/">Zur Startseite</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isOpen = isCurrentlyOpen();
  const hasImpressum = Boolean(
    studio.legal_company_name ||
    studio.legal_address ||
    studio.legal_email ||
    studio.legal_phone ||
    studio.legal_representative ||
    studio.legal_register ||
    studio.legal_register_number ||
    studio.legal_vat_id ||
    studio.legal_responsible ||
    studio.legal_imprint_extra ||
    studio.org_name ||
    studio.org_address ||
    studio.org_email ||
    studio.org_phone
  );
  const hasPrivacy = Boolean(String(studio.legal_privacy_text || "").trim());
  const hasTerms = Boolean(String(studio.legal_terms_text || "").trim());

  return (
    <div className="min-h-screen bg-white" data-testid="public-studio-page">
      <PracticeTracking orgSlug={studio.org_slug} />
      {/* Payment result modal */}
      <Dialog open={paymentModal.open} onOpenChange={(open) => {
        if (!open) {
          setPaymentModal({ open: false, type: null, message: "" });
          navigate(location.pathname, { replace: true });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${paymentModal.type === "success" ? "bg-green-100" : "bg-amber-100"}`}>
              {paymentModal.type === "success" ? <Check className="h-6 w-6 text-green-600" /> : <XCircle className="h-6 w-6 text-amber-600" />}
            </div>
            <DialogTitle className={`text-center ${paymentModal.type === "success" ? "text-green-600" : "text-amber-600"}`}>
              {paymentModal.type === "success" ? "Zahlung erfolgreich" : "Zahlung abgebrochen"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-center text-[#1D1D1F]">{paymentModal.message}</p>
          <Button className="w-full" onClick={() => { setPaymentModal({ open: false, type: null, message: "" }); navigate(location.pathname, { replace: true }); }}>
            OK
          </Button>
        </DialogContent>
      </Dialog>

      {/* Header Bild */}
      <section className="relative">
        {studio.header_image_url ? (
          <div className="w-full">
            <img
              src={
                studio.mobile_header_image_url && window.innerWidth < 768
                  ? studio.mobile_header_image_url
                  : studio.header_image_url
              }
              alt={studio?.name}
              className="w-full h-[52vh] min-h-[360px] max-h-[620px] md:h-[60vh] object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/10 flex flex-col items-center justify-center md:items-start md:justify-end px-4 md:px-10 md:pb-8">
              {studio.logo_url && (
                <img
                  src={studio.logo_url}
                  alt={`${studio?.name || ''} Logo`}
                  className={`w-32 h-32 object-contain mb-4 rounded-xl p-[2px] shadow-lg md:mb-3 ${studio.logo_background_color?'':'bg-white/90'}`}
                  style={studio.logo_background_color?{backgroundColor:studio.logo_background_color}:undefined}
                  loading="eager"
                />
              )}
              <h1 className="text-3xl md:text-5xl font-bold text-white text-center md:text-left px-4 md:px-0">{studio?.name}</h1>
            </div>
          </div>
        ) : (
          <div
            className="w-full h-48 md:h-64 flex flex-col items-center justify-center md:items-start md:justify-end px-4 md:px-10 md:pb-8"
            style={{ backgroundColor: theme.primary }}
          >
            {studio.logo_url && (
              <img
                src={studio.logo_url}
                alt={`${studio?.name || ''} Logo`}
                className={`w-32 h-32 object-contain mb-4 rounded-xl p-[2px] shadow-lg md:mb-3 ${studio.logo_background_color?'':'bg-white/90'}`}
                style={studio.logo_background_color?{backgroundColor:studio.logo_background_color}:undefined}
                loading="eager"
              />
            )}
            <h1 className="text-3xl md:text-5xl font-bold text-white text-center md:text-left px-4 md:px-0">{studio?.name}</h1>
          </div>
        )}
      </section>

      {/* Galerie Slider + Beschreibung */}
      {(galleryImages.length > 0 || studio.description) && (
        <section className="py-8 md:py-12 px-4 md:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              {/* Galerie Slider */}
              {galleryImages.length > 0 && (
                <div className="relative rounded-xl overflow-hidden shadow-lg">
                  <div className="aspect-video relative">
                    <img
                      src={galleryImages[currentSlide].image_url}
                      alt={`Galerie ${currentSlide + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {galleryImages.length > 1 && (
                      <>
                        <button
                          onClick={prevSlide}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          onClick={nextSlide}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                          {galleryImages.map((_, i) => (
                            <button
                              key={i}
                              onClick={() => setCurrentSlide(i)}
                              className={`w-2 h-2 rounded-full transition-colors ${
                                i === currentSlide ? "bg-white" : "bg-white/50"
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Beschreibung */}
              {studio.description && (
                <div className={galleryImages.length === 0 ? "lg:col-span-2" : ""}>
                  <h2 className="text-2xl font-bold mb-4" style={{ color: theme.primary }}>
                    Über uns
                  </h2>
                  <p className="text-gray-600 leading-relaxed whitespace-pre-line">{studio.description}</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Adresse + Öffnungszeiten + Anrufen */}
      <section className="py-8 px-4 md:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Adresse */}
            {studio.address && (
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <MapPin className="w-5 h-5" style={{ color: theme.primary }} />
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-gray-500 mb-1">Adresse</h3>
                  <p className="text-gray-800">{studio.address}</p>
                </div>
              </div>
            )}

            {/* Öffnungszeiten */}
            {availability.length > 0 && (
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Clock className="w-5 h-5" style={{ color: theme.primary }} />
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-gray-500 mb-1 flex items-center gap-2">
                    Öffnungszeiten
                    {isOpen ? (
                      <span className="flex items-center gap-1 text-green-600 text-xs">
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        Jetzt geöffnet
                      </span>
                    ) : (
                      <span className="text-red-500 text-xs">Geschlossen</span>
                    )}
                  </h3>
                  <div className="space-y-0.5 text-sm">
                    {(() => {
                      const groups = groupAvailability(availability);
                      const visibleGroups = showAllHours ? groups : groups.slice(0, 2);
                      const hiddenCount = Math.max(0, groups.length - 2);

                      return (
                        <>
                          {visibleGroups.map((g, idx) => (
                            <div key={idx} className="text-gray-600">
                              {formatGroupLabel(g)}: {formatDbTime(g.start_time)} - {formatDbTime(g.end_time)}
                            </div>
                          ))}

                          {!showAllHours && hiddenCount > 0 && (
                            <button
                              type="button"
                              className="text-xs text-gray-400 hover:underline mt-1"
                              onClick={() => setShowAllHours(true)}
                            >
                              +{hiddenCount} weitere
                            </button>
                          )}

                          {showAllHours && groups.length > 2 && (
                            <button
                              type="button"
                              className="text-xs text-gray-400 hover:underline mt-1"
                              onClick={() => setShowAllHours(false)}
                            >
                              Weniger anzeigen
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Anrufen Button */}
            {studio.phone && (
              <div className="flex flex-col items-start gap-3">
                <Button asChild size="lg" style={{ backgroundColor: theme.accent }} className="w-full md:w-auto text-white">
                  <a href={`tel:${studio.phone}`}>
                    <Phone className="w-4 h-4 mr-2" />
                    Jetzt anrufen
                  </a>
                </Button>

                {/* Social Media */}
                {(studio.facebook_url || studio.instagram_url || studio.tiktok_url) && (
                  <div className="flex items-center gap-2">
                    {studio.facebook_url && (
                      <a
                        href={studio.facebook_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors"
                        aria-label="Facebook"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                      </a>
                    )}
                    {studio.instagram_url && (
                      <a
                        href={studio.instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors"
                        aria-label="Instagram"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                        </svg>
                      </a>
                    )}
                    {studio.tiktok_url && (
                      <a
                        href={studio.tiktok_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors"
                        aria-label="TikTok"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                        </svg>
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Services/Leistungen */}
      <section className="py-10 px-4 md:px-8 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold" style={{ color: theme.primary }}>
            Unsere Leistungen
          </h2>

          <div className="w-full mt-6 border-b border-gray-200" />

          <div className="mt-6">
            {services.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Keine Leistungen verfügbar.</p>
            ) : (
              <div className="space-y-10">
                <div className="flex flex-wrap gap-2">
                  {categoryOptions.map((cat) => {
                    const active = selectedCategory === cat;
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setSelectedCategory(cat)}
                        className="focus:outline-none"
                      >
                        <Badge
                          className="px-3 py-1 text-sm rounded-full border"
                          style={
                            active
                               ? { backgroundColor: theme.primary, color: "white", borderColor: theme.primary }
                              : { backgroundColor: "white", color: "#4B5563", borderColor: "#E5E7EB" }
                          }
                        >
                          {cat}
                        </Badge>
                      </button>
                    );
                  })}
                </div>

                {serviceGroups.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Keine Leistungen in dieser Kategorie.</p>
                ) : (
                  <div className="space-y-10">
                    {serviceGroups.map((group) => (
                      <section key={group.id || group.name} className="space-y-4">
                        {(() => {
                          const isOpen = !shouldUseCategoryDropdowns || openCategoryNames.includes(String(group.name));
                          const header = group.banner_image_url ? (
                            <div className="relative w-full h-28 md:h-36 rounded-xl overflow-hidden border border-[#E5E7EB]">
                              <img
                                src={group.banner_image_url}
                                alt={group.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                              <div className="absolute inset-0 bg-black/35" />
                              <div className="absolute left-4 right-4 bottom-4 flex items-center justify-between gap-3">
                                <h3 className="text-white text-xl font-semibold">{group.name}</h3>
                                {shouldUseCategoryDropdowns ? (
                                  isOpen ? <ChevronUp className="h-5 w-5 text-white shrink-0" /> : <ChevronDown className="h-5 w-5 text-white shrink-0" />
                                ) : null}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-3 border-b border-[#E5E7EB] pb-4">
                              <div className="flex items-center gap-2 min-w-0">
                                <h3 className="text-xl font-semibold" style={{ color: theme.primary }}>
                                  {group.name}
                                </h3>
                                {shouldUseCategoryDropdowns ? (
                                  isOpen ? <ChevronUp className="h-5 w-5 shrink-0" style={{ color: theme.primary }} /> : <ChevronDown className="h-5 w-5 shrink-0" style={{ color: theme.primary }} />
                                ) : null}
                              </div>
                            </div>
                          );
                          if (!shouldUseCategoryDropdowns) return header;
                          return (
                            <button
                              type="button"
                              onClick={() => {
                                setOpenCategoryNames((current) =>
                                  current.includes(String(group.name))
                                    ? current.filter((name) => name !== String(group.name))
                                    : [...current, String(group.name)]
                                );
                              }}
                              className="w-full text-left"
                            >
                              {header}
                            </button>
                          );
                        })()}
                    {(!shouldUseCategoryDropdowns || openCategoryNames.includes(String(group.name))) ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {group.services.filter(Boolean).map((service) => {
                        const depositEnabled = Boolean(service.deposit_enabled);
                        const depositRequired = Boolean(service.deposit_required);
                        const isPaidService = Number(service.price || 0) > 0;

                        return (
                          <Card
                            key={service.id}
                            className="h-full overflow-hidden hover:shadow-lg transition-shadow cursor-pointer flex flex-col"
                            onClick={() => setBookingModal({ open: true, service })}
                          >
                            {service.image_url && (
                              <div className="h-44 overflow-hidden">
                                <img
                                  src={service.image_url}
                                  alt={service?.name}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              </div>
                            )}
                            <CardContent className="p-5 flex flex-1 flex-col">
                              <div className="flex justify-between items-start mb-2">
                                <h3 className="font-semibold text-lg">{service?.name}</h3>
                                {shouldShowServicePrice(service) ? (
                                  <span className="text-right">
                                    {hasComparePrice(service) ? (
                                      <span className="block text-xs text-[#86868B] line-through">
                                        {formatCurrency(service?.compare_price ?? 0, service?.currency || studio?.org_currency || studio?.currency || "EUR")}
                                      </span>
                                    ) : null}
                                    <span className="text-lg font-bold" style={{ color: theme.primary }}>
                                      {formatStudioServicePrice(service, service?.currency || studio?.org_currency || studio?.currency)}
                                    </span>
                                  </span>
                                ) : null}
                              </div>

                              <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                                <Clock className="w-4 h-4" />
                                <span>{service.duration} Minuten</span>
                              </div>

                              {service.description && (
                                <div className="mb-4">
                                  <p className={`text-sm text-gray-600 ${expandedDescriptions[service.id] ? "" : "line-clamp-2"}`}>{service.description}</p>
                                  {service.description.length > 120 && (
                                    <button
                                      type="button"
                                      className="mt-2 text-sm font-medium underline underline-offset-2"
                                      style={{ color: theme.accent }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedDescriptions((prev) => ({ ...prev, [service.id]: !prev[service.id] }));
                                      }}
                                    >
                                      {expandedDescriptions[service.id] ? "Weniger anzeigen" : "Mehr anzeigen"}
                                    </button>
                                  )}
                                </div>
                              )}

                              <div className="mt-auto pt-4 space-y-4">
                              {isPaidService && (depositEnabled || depositRequired || service.payment_type === "online") && (
                                <div>
                                  <Badge
                                    variant="secondary"
                                    style={
                                      service.payment_type === "online"
                                         ? { backgroundColor: "rgba(34, 197, 94, 0.15)", color: "#16a34a" }
                                        : { backgroundColor: `${theme.accent}10`, color: theme.accent }
                                    }
                                  >
                                    {service.payment_type === "online"
                                      ? depositRequired
                                        ? "Online Anzahlung nötig"
                                        : depositEnabled
                                          ? "Anzahlung optional"
                                          : "Online Zahlung nötig"
                                      : "Zahlung vor Ort"}
                                  </Badge>
                                </div>
                              )}

                              <Button
                                className="w-full"
                                style={{ backgroundColor: theme.accent, color: "white" }}
                              >
                                {service.requires_confirmation ? 'Termin anfragen' : 'Termin buchen'}
                              </Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                    ) : null}
                      </section>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Produkte */}
      {products.length > 0 && (
        <section className="py-12 px-4 md:px-8 bg-[#F6F6F8]">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <h2 className="text-3xl font-bold" style={{ color: theme.primary }}>
                  Produkte
                </h2>
                <p className="text-sm text-[#6E6E73] mt-1">Standort-Shop – direkt online kaufen</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {products.filter(Boolean).map((product) => {
                const stock = Number(product.stock_quantity ?? -1);
                const isOutOfStock = Number.isFinite(stock) && stock === 0;
                return (
                  <article
                    key={product.id}
                    className="group rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.03)] hover:shadow-[0_10px_24px_rgba(0,0,0,0.08)] transition-all"
                  >
                    <div className="aspect-[4/5] bg-[#F3F4F6] overflow-hidden relative">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product?.name}
                          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#9CA3AF] text-sm">Kein Bild</div>
                      )}
                      <div className="absolute top-3 left-3">
                        <Badge className={isOutOfStock ? "bg-[#FEE2E2] text-[#B91C1C]" : "bg-[#ECFDF3] text-[#027A48]"}>
                          {isOutOfStock ? "Ausverkauft" : "Verfügbar"}
                        </Badge>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-[17px] text-[#111827] leading-tight">{product.name}</h3>
                      <p className="text-sm text-[#6B7280] mt-1 min-h-[40px] line-clamp-2">
                        {product.description || "Pflegeprodukt aus unserem Standort-Sortiment."}
                      </p>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-xl font-bold tracking-tight" style={{ color: theme.primary }}>
                          {formatStudioMoney(product?.price ?? 0, product?.currency || studio?.org_currency || studio?.currency)}
                        </span>
                        <Button
                          className="rounded-xl"
                          style={{ backgroundColor: theme.accent, color: "white" }}
                          onClick={() => openProductOrder(product)}
                          disabled={isOutOfStock}
                        >
                          Kaufen
                        </Button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Google Reviews Carousel */}
      {googleReviews && googleReviews.reviews?.length > 0 && (
        <GoogleReviewsCarousel reviews={googleReviews} />
      )}

      {/* Footer */}
      <footer className="py-10 px-4 md:px-8 text-white" style={{ backgroundColor: theme.primary }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Kontakt */}
            <div>
              <h3 className="font-semibold text-lg mb-4">Kontakt</h3>
              <div className="space-y-2 text-white/80">
                {studio.email && (
                  <a href={`mailto:${studio.email}`} className="flex items-center gap-2 hover:text-white">
                    <Mail className="w-4 h-4" />
                    {studio.email}
                  </a>
                )}
                {studio.phone && (
                  <a href={`tel:${studio.phone}`} className="flex items-center gap-2 hover:text-white">
                    <Phone className="w-4 h-4" />
                    {studio.phone}
                  </a>
                )}
                {studio.address && (
                  <p className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-1" />
                    {studio.address}
                  </p>
                )}
              </div>
            </div>

            {/* Öffnungszeiten */}
            {availability.length > 0 && (
              <div>
                <h3 className="font-semibold text-lg mb-4">Öffnungszeiten</h3>
                <div className="space-y-1 text-sm text-white/80">
                  {WEEKDAYS.map((day, i) => {
                    const avail = availability.find((a) => a.weekday === i);
                    const isToday = new Date().getDay() === (i === 6 ? 0 : i + 1);
                    const now = new Date();
                    const currentTime = `${now.getHours().toString().padStart(2, "0")}:${now
                      .getMinutes()
                      .toString()
                      .padStart(2, "0")}`;
                    const isOpenNow =
                      avail &&
                      isToday &&
                      currentTime >= avail.start_time &&
                      currentTime <= avail.end_time;

                    return (
                      <div
                        key={i}
                        className={`flex justify-between ${isToday ? "font-semibold text-white" : ""}`}
                      >
                        <span className="flex items-center gap-1">
                          {day}
                          {isOpenNow && (
                            <span className="ml-2 px-1.5 py-0.5 text-xs bg-green-500 text-white rounded">
                              Geöffnet
                            </span>
                          )}
                        </span>
                        <span className={isOpenNow ? "text-green-400" : ""}>
                          {avail
                             ? `${formatDbTime(avail.start_time)} - ${formatDbTime(avail.end_time)}`
                            : "Geschlossen"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Google Map */}
            <div>
              <h3 className="font-semibold text-lg mb-4">Standort</h3>

              {studio.address ? (
                <div className="rounded-lg overflow-hidden h-40">
                  <iframe
                    title="Standort"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(
                      studio.address
                    )}`}
                    allowFullScreen
                  />
                </div>
              ) : (
                <p className="text-white/60 text-sm">Adresse nicht verfügbar</p>
              )}
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-8 pt-6 border-t border-white/20 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/60">
            <img src="/images/mib-logo-full.png" alt="Make It Booking" className="h-6 w-auto object-contain opacity-90 invert" />
            <div className="flex items-center gap-4">
              {hasImpressum && (
                <Dialog>
                <DialogTrigger asChild>
                  <button type="button" className="underline hover:text-white">
                    Impressum
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Impressum</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 text-sm text-muted-foreground">
                    <p>Angaben gemaess Paragraph 5 TMG</p>
                    <div className="space-y-1 text-sm text-foreground">
                        {(studio.legal_company_name || studio.org_name) && (
                          <p>{studio.legal_company_name || studio.org_name}</p>
                        )}
                        {studio.legal_representative && (
                          <p>Vertreten durch: {studio.legal_representative}</p>
                        )}
                        {(studio.legal_address || studio.org_address) && (
                          <p>{studio.legal_address || studio.org_address}</p>
                        )}
                        {(studio.legal_email || studio.org_email) && (
                          <p>{studio.legal_email || studio.org_email}</p>
                        )}
                        {(studio.legal_phone || studio.org_phone) && (
                          <p>{studio.legal_phone || studio.org_phone}</p>
                        )}
                        {studio.legal_register && studio.legal_register_number && (
                          <p>{studio.legal_register}: {studio.legal_register_number}</p>
                        )}
                        {studio.legal_vat_id && <p>USt-IdNr.: {studio.legal_vat_id}</p>}
                        {studio.legal_responsible && (
                          <p>Verantwortlich i.S.d. Paragraph 55 RStV: {studio.legal_responsible}</p>
                        )}
                        {studio.legal_imprint_extra && <LinkifiedText>{studio.legal_imprint_extra}</LinkifiedText>}
                      </div>
                  </div>
                </DialogContent>
                </Dialog>
              )}

              {hasPrivacy && (
                <Dialog>
                <DialogTrigger asChild>
                  <button type="button" className="underline hover:text-white">
                    Datenschutz
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Datenschutz</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 text-sm text-muted-foreground">
                    <LinkifiedText className="text-foreground">{studio.legal_privacy_text}</LinkifiedText>
                  </div>
                </DialogContent>
                </Dialog>
              )}

              {hasTerms && (
                <Dialog>
                <DialogTrigger asChild>
                  <button type="button" className="underline hover:text-white">
                    AGB
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>AGB</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 text-sm text-muted-foreground">
                    <LinkifiedText className="text-foreground">{studio.legal_terms_text}</LinkifiedText>
                  </div>
                </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </div>
      </footer>

      <BookingModal
        open={bookingModal.open}
        onClose={() => setBookingModal({ open: false, service: null })}
        orgSlug={studio.org_slug}
        org={{
          name: studio.org_name,
          slug: studio.org_slug,
          currency: studio.org_currency || studio.currency || "EUR",
          terminology: studio.terminology,
          gift_cards_enabled: Boolean(studio.gift_cards_enabled),
          goae_billing_enabled: Boolean(studio.goae_billing_enabled),
          legal_privacy_text: studio.legal_privacy_text || "",
        }}
        service={bookingModal.service}
        theme={theme}
        locationId={studio.id}
        locationName={studio.name}
        autoAssignStaff={studio.booking_auto_assign_staff !== false}
        returnPath={`/studio/${slug}`}
        successRedirectUrl={studio.booking_success_redirect_url || ""}
      />

      <Dialog open={productOrderDialogOpen} onOpenChange={setProductOrderDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Produkt kaufen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-[#F5F5F7]">
              <p className="font-medium text-[#1D1D1F]">{selectedProduct?.name || "Produkt"}</p>
              <p className="text-sm text-[#86868B]">{formatStudioMoney(selectedProduct?.price ?? 0, selectedProduct?.currency || studio?.org_currency || studio?.currency)} pro Stück</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Menge</label>
              <Input
                type="number"
                min="1"
                value={productOrderForm.quantity}
                onChange={(e) => setProductOrderForm((p) => ({ ...p, quantity: e.target.value }))}
                className="input-base"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Name *</label>
              <Input
                value={productOrderForm.client_name}
                onChange={(e) => setProductOrderForm((p) => ({ ...p, client_name: e.target.value }))}
                className="input-base"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">E-Mail *</label>
              <Input
                type="email"
                value={productOrderForm.client_email}
                onChange={(e) => setProductOrderForm((p) => ({ ...p, client_email: e.target.value }))}
                className="input-base"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Telefon</label>
              <Input
                value={productOrderForm.client_phone}
                onChange={(e) => setProductOrderForm((p) => ({ ...p, client_phone: e.target.value }))}
                className="input-base"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Straße *</label>
              <Input
                value={productOrderForm.client_street}
                onChange={(e) => setProductOrderForm((p) => ({ ...p, client_street: e.target.value }))}
                className="input-base"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">PLZ *</label>
                <Input
                  value={productOrderForm.client_postal_code}
                  onChange={(e) => setProductOrderForm((p) => ({ ...p, client_postal_code: e.target.value }))}
                  className="input-base"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Stadt *</label>
                <Input
                  value={productOrderForm.client_city}
                  onChange={(e) => setProductOrderForm((p) => ({ ...p, client_city: e.target.value }))}
                  className="input-base"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Notiz</label>
              <Textarea
                value={productOrderForm.notes}
                onChange={(e) => setProductOrderForm((p) => ({ ...p, notes: e.target.value }))}
                className="input-base min-h-[80px]"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setProductOrderDialogOpen(false)}>Abbrechen</Button>
              <Button className="btn-primary" onClick={submitProductOrder} disabled={productOrderLoading}>
                {productOrderLoading ? "Weiterleitung..." : "Jetzt bezahlen"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
